import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, tap } from 'rxjs/operators';
import { AppConfig } from 'src/app/core/services/app-config.service';
import { DialogService } from '../../../core/services/dialog.service';
import { PaymentFragment } from '../../../pay/models/payment-fragment.model';
import { PaymentStatus } from '../../../pay/models/payment-status.model';
import { PaymentStep } from '../../../pay/models/payment-step.model';
import { Payment, PaymentType } from '../../../pay/models/payment.model';
import { PaymentService } from '../../../pay/services/payment.service';
import { DEFAULT_PATTERN } from '../../configs/date-config';
import { CouponInfoSnackBarService } from 'src/app/pay/services/coupon-info-snackbar.service';
import { LiabilitiesPaymentType } from '../../../liabilities/models/liabilities-payment-type.enum';
import { MatExpansionPanel } from '@angular/material/expansion';

@Component({
standalone: false,
  selector: 'app-payment-summary',
  styleUrls: ['./payment-summary.component.scss'],
  templateUrl: './payment-summary.component.html',
})
export class PaymentSummaryComponent implements OnChanges {
  @Input() public payment: Payment;
  @Input() public paymentType: PaymentType;
  @Input() public emptyCartCouponId: string;
  @Input() public couponIdFor: string;
  @Input() public paymentMethodSelected: boolean;
  @Input() public enableRedirectToFragment: boolean;
  @Input() public paymentUrl: string;
  @Input() public cancelPaymentRedirectUrl: string;
  @Input() public additionalCommission: number;
  @Input() public startedPayment: boolean;

  private _errorMsg: { value: string, isHtml?: boolean, state?: any };
  @Input() set errorMsg(value: { value: string, url?: string, state?: any }) {
    this._errorMsg = value;
    if (value !== null && value.value !== null && value.value.length > 0) {
      this.liabilitiesList.open();
    }
  }

  get errorMsg() {
    return this._errorMsg;
  }

  public paymentStatus = PaymentStatus;
  public PaymentStatus = PaymentStatus;
  public LiabilitiesPaymentType = LiabilitiesPaymentType;
  public buttonLoading = false;
  public step: PaymentStep = PaymentStep.METHODS;

  public defaultPattern: string = DEFAULT_PATTERN;

  public commission: number;
  public totalAmount: number;
  public showCloseDueDateInfo: boolean;

  @ViewChild(MatExpansionPanel) liabilitiesList: MatExpansionPanel;

  constructor(
    private router: Router,
    private paymentService: PaymentService,
    private dialog: DialogService,
    public appConfig: AppConfig,
    private couponInfoSnackBarService: CouponInfoSnackBarService,
  ) {
  }


  public ngOnChanges(): void {
    this.setCommisionAndAmount();
  }

  public shoudBeExpanded() {
    return this.payment.paymentFragments.length === 1 || this.startedPayment;
  }

  private setCommisionAndAmount() {
    const additionalCommission =
      this.paymentType === PaymentType.EXPRESS ? this.appConfig.config.payment.express.commissionAmount : 0;

    this.commission = this.calculateCommission(additionalCommission);
    this.totalAmount = this.calculateTotalAmount(additionalCommission);
  }

  private calculateCommission(additionalCommission: number): number {
    return !this.payment ? 0 : this.payment.commission + additionalCommission;
  }

  private calculateTotalAmount(additionalCommission: number): number {
    if (!this.payment) {
      return 0;
    }
    return this.payment.status === PaymentStatus.CREATED
      ? this.payment.totalAmount + additionalCommission
      : this.payment.totalAmount;
  }

  public removeFromCart(payment: Payment, fragment: PaymentFragment): void {
    if (payment.status === PaymentStatus.CREATED) {
      this.remove(payment, fragment);
    }
  }

  private remove(payment: Payment, fragment: PaymentFragment): void {
    this.modalRemove(payment.paymentFragments.length, payment.id, fragment.liability.id);
  }

  public checkCouponInfo(payment: Payment): void {
    if (payment.couponInfo && Object.keys(payment.couponInfo).length > 0) {
      this.couponInfoSnackBarService.showCouponInfo(payment);
    }
  }

  private modalRemove(fragmentsLength: number, paymentId: string, liabilityId: string): void {
    fragmentsLength === 1 ? this.modalCancelPayment(paymentId) : this.modalRemoveFromCart(paymentId, liabilityId);
  }

  private modalCancelPayment(paymentId: string): void {
    this.paymentService
      .cancelPayment(paymentId)
      .pipe(
        tap(() => this.couponInfoSnackBarService.closeCouponInfo()),
        switchMap(() => this.router.navigate([this.cancelPaymentRedirectUrl])),
        tap(() => this.paymentService.refreshBasePayment()),
        tap(() => (this.buttonLoading = false)),
      )
      .subscribe();
  }

  private modalRemoveFromCart(paymentId: string, liabilityId: string): void {
    this.paymentService
      .recreatePayment(paymentId, { removedLiabilityIds: [liabilityId] })
      .pipe(
        tap((newPaymentId) => {
          this.router.navigate([this.paymentUrl, newPaymentId]);
          this.buttonLoading = false;
        }),
        tap(() => this.paymentService.refreshBasePayment()),
        tap(() => (this.buttonLoading = false)),
      )
      .subscribe();
  }

  public redirectToFragment(liabilityId: string): void {
    if (!this.startedPayment && this.enableRedirectToFragment) {
      this.router.navigate(['/liabilities', liabilityId], {
        queryParams: {
          returnUrl: `/liabilities/pay/${this.payment.id}`,
        },
      });
    }
  }

  public get countPaymentFragments(): number {
    return this.payment ? this.payment.paymentFragments.length : 0;
  }
}
